import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    Alert,
    LinearProgress,
    IconButton,
    Chip,
    Paper,
    Snackbar,
    Grid,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon,
    Support as SupportIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    MarkEmailRead as MarkReadIcon,
    NotificationsActive as NotificationsActiveIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    MobileFriendly as MobileIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { get, post } from '../services/apiService';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { subscribeUserToPush, isPushNotificationsSupported } from '../utils/pushNotificationManager';
import { useNavigate } from 'react-router-dom';

function ClientNotifications() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { client } = useClientAuth();

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    // Estado del permiso de notificación nativo del navegador
    const [pushPermission, setPushPermission] = useState('default');
    const [isPushSupported, setIsPushSupported] = useState(true);

    // Actualizar estado de permisos
    const updatePermissionStatus = useCallback(() => {
        setIsPushSupported(isPushNotificationsSupported());
        if ('Notification' in window) {
            setPushPermission(Notification.permission);
        }
    }, []);

    // Cargar notificaciones desde la API
    const fetchNotifications = useCallback(async (showLoader = true) => {
        if (!client) return;

        try {
            if (showLoader) setLoading(true);
            setError(null);
            const data = await get('/api/client/notifications', true);
            if (Array.isArray(data)) {
                setNotifications(data);
            }
        } catch (err) {
            console.error('Error al cargar notificaciones:', err);
            setError('No se pudieron cargar las notificaciones. Inténtelo de nuevo.');
        } finally {
            if (showLoader) setLoading(false);
        }
    }, [client]);

    useEffect(() => {
        fetchNotifications();
        updatePermissionStatus();

        // Verificar permisos al enfocar la ventana
        window.addEventListener('focus', updatePermissionStatus);
        return () => {
            window.removeEventListener('focus', updatePermissionStatus);
        };
    }, [fetchNotifications, updatePermissionStatus]);

    // Habilitar notificaciones Push en el móvil mediante gesto del usuario
    const handleEnablePush = async () => {
        try {
            setLoading(true);
            const success = await subscribeUserToPush();
            updatePermissionStatus();

            if (success) {
                setToast({
                    open: true,
                    message: '¡Excelente! Notificaciones activadas exitosamente en tu celular.',
                    severity: 'success'
                });
            } else {
                // Verificar si denegó el permiso
                if ('Notification' in window && Notification.permission === 'denied') {
                    setToast({
                        open: true,
                        message: 'Permiso denegado. Por favor, actívalas manualmente en los ajustes de tu navegador.',
                        severity: 'warning'
                    });
                } else {
                    setToast({
                        open: true,
                        message: 'No se pudo completar el registro de notificaciones.',
                        severity: 'error'
                    });
                }
            }
        } catch (err) {
            console.error('Error al activar notificaciones push:', err);
            setToast({
                open: true,
                message: 'Ocurrió un error inesperado al solicitar permisos.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Marcar una notificación como leída
    const handleMarkAsRead = async (id) => {
        try {
            await post(`/api/client/notifications/${id}/read`, {}, true);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, read: true } : n)
            );
            setToast({
                open: true,
                message: 'Notificación marcada como leída.',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error al marcar leída:', err);
            setToast({
                open: true,
                message: 'No se pudo actualizar la notificación.',
                severity: 'error'
            });
        }
    };

    // Marcar todas como leídas
    const handleMarkAllAsRead = async () => {
        try {
            setLoading(true);
            await post('/api/client/notifications/read-all', {}, true);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setToast({
                open: true,
                message: 'Todas las notificaciones se marcaron como leídas.',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error al marcar todas leídas:', err);
            setToast({
                open: true,
                message: 'No se pudieron actualizar las notificaciones.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Icono correspondiente al tipo de notificación
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'payment':
            case 'payment_due':
            case 'payment_upcoming':
                return <PaymentIcon />;
            case 'invoice':
            case 'invoice_created':
                return <ReceiptIcon />;
            case 'support':
                return <SupportIcon />;
            case 'service_cut':
                return <WarningIcon color="error" />;
            default:
                return <InfoIcon />;
        }
    };

    // Obtener la severidad según el tipo
    const getNotificationSeverity = (type) => {
        switch (type) {
            case 'service_cut':
                return 'error';
            case 'payment_due':
                return 'warning';
            case 'payment_upcoming':
            case 'invoice_created':
                return 'info';
            default:
                return 'info';
        }
    };

    // Formatear fecha relativa amigable en español
    const formatRelativeTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Hace unos instantes';
        if (diffMins < 60) return `Hace ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;

        const diffHours = Math.floor(diffMs / 3600000);
        if (diffHours < 24) return `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;

        const diffDays = Math.floor(diffMs / 86400000);
        if (diffDays < 7) return `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;

        return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Acción para redireccionar cuando hacen clic en una notificación
    const handleNotificationClick = (notification) => {
        if (notification.id && !notification.read) {
            handleMarkAsRead(notification.id);
        }

        // Redireccionar si aplica
        const type = notification.type;
        const title = notification.title || '';
        if (type === 'invoice' || type === 'invoice_created') {
            navigate('/client/invoices');
        } else if (type === 'payment' || type === 'payment_due' || type === 'payment_upcoming' || type === 'service_cut') {
            navigate('/client/payments');
        } else if (type === 'support') {
            navigate('/client/support');
        } else if (type === 'promotion' || type === 'promotion_created' || title.toLowerCase().includes('oferta') || title.toLowerCase().includes('promoción') || title.toLowerCase().includes('promo')) {
            navigate('/client/promotions');
        } else {
            navigate('/client/dashboard');
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, margin: '0 auto' }}>
            {/* Cabecera / Botón Atrás */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                <IconButton onClick={() => navigate('/client/dashboard')} color="primary">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" fontWeight="bold">
                    Mis Notificaciones
                </Typography>
            </Box>

            {/* CARD 1: Web Push Subscription Controller (Celular y Navegador) */}
            <Card sx={{
                mb: 4,
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(21, 101, 192, 0.25)'
            }}>
                <CardContent sx={{ py: 3, px: { xs: 2, md: 3 } }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={8}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                <MobileIcon sx={{ fontSize: 28 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    Notificaciones en tu Celular
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                                Recibe alertas de vencimiento, confirmación de pagos y avisos importantes directo en tu teléfono móvil en tiempo real.
                              </Typography>

                            {/* Badge dinámico de estado */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {!isPushSupported ? (
                                    <Chip 
                                        icon={<BlockIcon style={{ color: 'white' }} />} 
                                        label="No soportado en este dispositivo" 
                                        size="small" 
                                        sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', border: '1px dashed rgba(255,255,255,0.3)' }} 
                                    />
                                ) : pushPermission === 'granted' ? (
                                    <Chip 
                                        icon={<CheckCircleIcon style={{ color: 'white' }} />} 
                                        label="Notificaciones Activas en este Móvil" 
                                        size="small" 
                                        sx={{ bgcolor: 'rgba(76, 175, 80, 0.4)', color: 'white', border: '1px solid #4caf50' }} 
                                    />
                                ) : pushPermission === 'denied' ? (
                                    <Chip 
                                        icon={<BlockIcon style={{ color: 'white' }} />} 
                                        label="Permiso Bloqueado en el Navegador" 
                                        size="small" 
                                        sx={{ bgcolor: 'rgba(244, 67, 54, 0.4)', color: 'white', border: '1px solid #f44336' }} 
                                    />
                                ) : (
                                    <Chip 
                                        icon={<NotificationsActiveIcon style={{ color: 'white' }} />} 
                                        label="Pendiente de Activación" 
                                        size="small" 
                                        sx={{ bgcolor: 'rgba(255, 152, 0, 0.4)', color: 'white', border: '1px solid #ff9800' }} 
                                    />
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                            {isPushSupported && pushPermission !== 'granted' && (
                                <Button
                                    variant="contained"
                                    onClick={handleEnablePush}
                                    disabled={loading}
                                    sx={{
                                        bgcolor: 'white',
                                        color: '#1565c0',
                                        fontWeight: 'bold',
                                        px: 3,
                                        py: 1,
                                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                        '&:hover': {
                                            bgcolor: '#e3f2fd',
                                            boxShadow: '0 6px 14px rgba(0,0,0,0.2)'
                                        }
                                    }}
                                >
                                    {pushPermission === 'denied' ? 'Reactivar en Ajustes' : 'Activar en mi Celular'}
                                </Button>
                            )}
                        </Grid>
                    </Grid>

                    {/* Alerta explicativa si está bloqueado */}
                    {pushPermission === 'denied' && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, border: '1px dashed rgba(255,255,255,0.2)' }}>
                            <Typography variant="caption" sx={{ display: 'block', color: '#ffc107', fontWeight: 'medium' }}>
                                ⚠️ Las notificaciones están bloqueadas en tu navegador. Para recibirlas:
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                1. Presiona el candado o el ícono de ajustes al lado de la barra de direcciones.
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                2. Activa el interruptor de "Notificaciones".
                            </Typography>
                        </Box>
                    )}

                    {/* Zona de Diagnóstico Técnico para Desarrollador/Administrador */}
                    <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.15)', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            🔧 <strong>Zona de Diagnóstico Técnico:</strong> ¿Quieres probar el envío push directamente desde esta sesión?
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                href={`/api/client/notifications/debug-push?token=${localStorage.getItem('clientToken')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                    borderColor: 'rgba(255,255,255,0.4)', 
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: 'white',
                                        bgcolor: 'rgba(255,255,255,0.08)'
                                    }
                                }}
                            >
                                Ver Estado Técnico (JSON)
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                href={`/api/client/notifications/debug-push?token=${localStorage.getItem('clientToken')}&test=true`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                    borderColor: 'rgba(255,255,255,0.4)', 
                                    color: '#ffc107',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: '#ffc107',
                                        bgcolor: 'rgba(255,255,255,0.08)'
                                    }
                                }}
                            >
                                Enviar Notificación de Prueba 🔔
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* CARD 2: Lista Completa de Notificaciones */}
            <Card>
                <CardContent sx={{ p: 0 }}>
                    {/* Header de la Lista */}
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(0, 0, 0, 0.02)', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Historial Reciente
                            <IconButton size="small" onClick={() => fetchNotifications(true)} disabled={loading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Typography>
                        {notifications.some(n => !n.read) && (
                            <Button
                                size="small"
                                startIcon={<MarkReadIcon />}
                                onClick={handleMarkAllAsRead}
                                disabled={loading}
                                sx={{ textTransform: 'none' }}
                            >
                                Marcar todas como leídas
                            </Button>
                        )}
                    </Box>

                    {loading && <LinearProgress />}

                    {error && (
                        <Box sx={{ p: 3 }}>
                            <Alert severity="error">{error}</Alert>
                        </Box>
                    )}

                    {/* Renderizado de notificaciones */}
                    {notifications.length === 0 ? (
                        <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
                            <Avatar sx={{ width: 60, height: 60, bgcolor: 'rgba(0, 0, 0, 0.05)', color: 'text.disabled', mx: 'auto', mb: 2 }}>
                                <NotificationsIcon sx={{ fontSize: 32 }} />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight="medium" color="text.secondary">
                                No tienes notificaciones registradas
                            </Typography>
                            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                                Las alertas y novedades sobre tu servicio aparecerán aquí.
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {notifications.map((notification, index) => {
                                const severity = getNotificationSeverity(notification.type);
                                const isUnread = !notification.read;

                                return (
                                    <React.Fragment key={notification.id}>
                                        <ListItem
                                            alignItems="flex-start"
                                            onClick={() => handleNotificationClick(notification)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: isUnread ? 'rgba(21, 101, 192, 0.03)' : 'transparent',
                                                borderLeft: isUnread ? '4px solid' : '4px solid transparent',
                                                borderColor: severity === 'error' ? 'error.main' :
                                                              severity === 'warning' ? 'warning.main' : 'primary.main',
                                                py: 2,
                                                px: { xs: 2, md: 3 },
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    bgcolor: 'action.hover',
                                                    transform: 'translateX(4px)'
                                                }
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{
                                                    bgcolor: theme => {
                                                        return severity === 'error' ? theme.palette.error.light :
                                                               severity === 'warning' ? theme.palette.warning.light :
                                                               theme.palette.primary.light;
                                                    },
                                                    color: theme => {
                                                        return severity === 'error' ? theme.palette.error.dark :
                                                               severity === 'warning' ? theme.palette.warning.dark :
                                                               theme.palette.primary.dark;
                                                    }
                                                }}>
                                                    {getNotificationIcon(notification.type)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{ fontWeight: isUnread ? 'bold' : 'medium', color: isUnread ? 'text.primary' : 'text.secondary' }}
                                                        >
                                                            {notification.title}
                                                        </Typography>
                                                        {isUnread && (
                                                            <Chip 
                                                                label="Nueva" 
                                                                size="small" 
                                                                color="primary" 
                                                                sx={{ height: 18, fontSize: '0.625rem', fontWeight: 'bold' }} 
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 0.5 }}>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.primary"
                                                            sx={{ fontSize: '0.875rem', lineHeight: 1.4, mb: 0.8 }}
                                                        >
                                                            {notification.message}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ fontSize: '0.75rem' }}
                                                            >
                                                                {formatRelativeTime(notification.created_at)}
                                                            </Typography>
                                                            {isUnread && (
                                                                <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleMarkAsRead(notification.id);
                                                                    }}
                                                                    sx={{ fontSize: '0.75rem', p: 0, minWidth: 0, textTransform: 'none' }}
                                                                >
                                                                    Marcar como leída
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        {index < notifications.length - 1 && <Divider component="li" />}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    )}
                </CardContent>
            </Card>

            {/* Notificaciones flotantes Toast */}
            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setToast(prev => ({ ...prev, open: false }))}
                    severity={toast.severity}
                    variant="filled"
                    sx={{ width: '100%', borderRadius: 2 }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ClientNotifications;
